import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { BANKS_ICONS_NAMES, DictTypes } from '@/constants';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import type { CreateAutoCreditProps } from '../../../types/form-types';
import type {
  IPromotion,
  StockBoundElementType
} from '../../../types/products';

const useStockBoundInfo = () => {
  const { control } = useFormContext<CreateAutoCreditProps>();
  const markModelYear = useWatch({ control, name: 'markModelYear' });

  const { dicts } = useAppSelector((state) => state.dict);

  const [infoModalData, setInfoModalData] = useState<IPromotion | null>(null);

  const bankIconName =
    BANKS_ICONS_NAMES[
      infoModalData?.bank?.name as keyof typeof BANKS_ICONS_NAMES
    ];

  const handleOpenInfoModal = (data: StockBoundElementType) => {
    const bankInfo = dicts?.[DictTypes.Banks]?.getBy.uuid[
      safeKey(data.bankUuid)
    ] || {
      fullname: data.bank,
      code: data.bankUuid,
      icon: null,
      name: data.bank
    };

    const formattedData: IPromotion = {
      uuid: data.uuid,
      name: data.name,
      code: data.code,
      promo_start_date: data.promoStartDate || '',
      promo_end_date: data.promoEndDate || '',
      percent_from: data.percentFrom ?? null,
      bank: {
        fullname: bankInfo.fullname,
        code: String(bankInfo.code),
        icon: bankInfo?.icon || bankIconName?.icon || '',
        name: bankInfo.name
      },
      Products_Info:
        data.productConditions?.map((condition) => ({
          is_active: condition.isActive,
          is_proof_income: condition.isProofIncome,
          loan_amount_from: condition.loanAmountFrom ?? '0',
          loan_amount_to: condition.loanAmountTo ?? '0',
          initial_payment_from: condition.initialPaymentFrom,
          initial_payment_to: condition.initialPaymentTo,
          loan_period_from: Number(condition.loanPeriodFrom) || 0,
          loan_period_to: Number(condition.loanPeriodTo) || 0
        })) ?? [],

      Promo_Products_Models: data.productModels.map((item) => {
        const modelInfo = {
          name: item.model.name,
          code: item.model.code,
          img_src: item.model.imgSrc || ''
        };

        const brandInfo = dicts?.[DictTypes.Brands]?.getBy.name[
          safeKey(item.model.brand.name)
        ] || {
          name: '',
          code: 0,
          img_src: ''
        };
        return {
          is_active: true,
          model: {
            name: modelInfo.name,
            code: modelInfo.code,
            img_src: modelInfo.img_src,
            brand: {
              name: brandInfo.name,
              code: Number(brandInfo.code),
              img_src: brandInfo.img_src || ''
            }
          }
        };
      })
    };

    setInfoModalData(formattedData);
  };

  const stockBoundInfoList = useMemo(() => {
    if (!infoModalData) return [];
    const result: Array<Array<{ name: string; value: string }>> = [];

    infoModalData.Products_Info?.forEach((item) => {
      const condition = [];

      if (item?.initial_payment_from || item?.initial_payment_to) {
        const fromText = item?.initial_payment_from
          ? `от ${item?.initial_payment_to}% `
          : '';
        const toText = item?.initial_payment_to
          ? `до ${item?.initial_payment_to}%`
          : '';
        condition.push({
          name: 'Первоначальный взнос:',
          value: `${fromText}${toText}`
        });
      }
      if (item?.loan_amount_from || item?.initial_payment_to) {
        const fromText = item?.loan_amount_from
          ? `от ${Number(item?.loan_amount_from).toLocaleString('ru-RU')} `
          : '';
        const toText = item?.initial_payment_to
          ? `до ${Number(item?.initial_payment_to).toLocaleString('ru-RU')}`
          : '';
        condition.push({
          name: 'Сумма займа тг.:',
          value: `${fromText}${toText}`
        });
      }
      if (item?.loan_period_from || item?.loan_period_to) {
        const fromText = item?.loan_period_from
          ? `от ${item?.loan_period_from} `
          : '';
        const toText = item?.loan_period_to ? `до ${item?.loan_period_to}` : '';
        condition.push({
          name: 'Срок кредита:',
          value: `${fromText}${toText}`
        });
      }
      if (typeof item?.is_proof_income === 'boolean')
        condition.push({
          name: 'Подтверждение дохода',
          value: item?.is_proof_income ? 'Да' : 'Нет'
        });

      result.push(condition);
    });

    const generalInfo = [
      {
        name: 'Бренд:',
        value:
          infoModalData.Promo_Products_Models?.[0]?.model.brand.name ||
          markModelYear?.brand?.name ||
          ''
      },
      {
        name: 'Модели авто:',
        value:
          infoModalData.Promo_Products_Models?.reduce((result, item) => {
            if (result) result += `, ${item.model.name}`;
            else result = item.model.name;
            return result;
          }, '') ||
          markModelYear?.model?.name ||
          ''
      }
    ];
    result.push(generalInfo);

    return result;
  }, [infoModalData, markModelYear]);

  return {
    infoModalData,
    setInfoModalData,
    stockBoundInfoList,
    handleOpenInfoModal
  };
};

export default useStockBoundInfo;
